import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, ChevronDown, CreditCard, FileText } from "lucide-react";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { MethodIcon } from "@/components/billing/PayIcon";
import { PaymentActivity } from "@/components/billing/PaymentActivity";
import { PaymentAttempts } from "@/components/billing/PaymentAttempts";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { DomainStatusBadge } from "@/components/status";
import { FeatureTip } from "@/components/whats-new/FeatureTip";
import {
  Alert,
  Badge,
  Button,
  buttonClass,
  CopyLinkButton,
  cx,
  EmptyState,
  ErrorAlert,
  ErrorState,
  Field,
  Form,
  FullPageSpinner,
  Input,
  LinkButton,
  Modal,
  PageHeader,
  Pager,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  useCursorPager,
  usePrefetchNext,
  useToast,
} from "@/components/ui";
import { AUTO_REFRESH_MS, DataFreshness, freshnessOf } from "@/components/ui/DataFreshness";
import { missingPermissionReason } from "@/lib/access";
import { portal } from "@/lib/api";
import { approvalNeededBy, approvePayment } from "@/lib/approval";
import { downloadBlob } from "@/lib/download";
import { formatDate, formatDayMonth, formatMoney } from "@/lib/format";
import { methodDetail, methodProblem, payableDefault } from "@/lib/payments";
import { useOrganizationDomains } from "@/lib/queries";
import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import type { CustomerAccount, Invoice, InvoiceSummary, PaymentMethod, Subscription } from "@/lib/types";
import { choice, useUrlView } from "@/lib/url-state";
import { InvoiceStatusPill } from "@/components/billing/parts";

const TABS = [
  ["overview", "Overview"],
  ["invoices", "Invoices"],
  ["payment-activity", "Payment activity"],
  ["payment-methods", "Payment methods"],
] as const;
type Tab = (typeof TABS)[number][0];

const panel = "rounded-[18px] border border-border bg-surface";
const pill = "inline-flex items-center rounded-full px-4 py-[9px] text-[13px] font-semibold";
const secondaryPill = "border border-border bg-surface text-fg hover:bg-muted";
/** A failed invoice still owed. An upgrade whose Direct Debit failed isn't: the plan never changed. */
const owed = (i: Invoice) => i.status === "failed" && i.kind !== "upgrade";
const planName = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);
const shortDate = formatDayMonth;

/** Figma "Billing — Overview" and "Billing — Invoices": the plan, renewals, charges, cards and invoices. */
export function BillingPage() {
  const { orgId, current, can, permissionsLoading } = useOrganization();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some(([t]) => t === params.get("tab")) ? (params.get("tab") as Tab) : "overview";
  const canRead = can("billing:read");
  const sub = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => portal.subscription(orgId),
    enabled: canRead,
    refetchInterval: AUTO_REFRESH_MS,
  });
  // The overview's few invoice facts (newest three, what is owed, an upgrade waiting) come from one small read, not
  // from every invoice: the list itself pages in InvoicesTab.
  const invoices = useQuery({
    queryKey: ["invoices", orgId, "summary"],
    queryFn: () => portal.invoiceSummary(orgId),
    enabled: canRead,
    refetchInterval: AUTO_REFRESH_MS,
  });
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
    enabled: canRead,
    refetchInterval: AUTO_REFRESH_MS,
  });
  // Refresh also reloads the invoice page on screen in InvoicesTab, which keeps its own query.
  const qc = useQueryClient();
  const freshness = freshnessOf(sub, invoices, methods);
  const refreshAll = () =>
    Promise.all([
      freshness.onRefresh(),
      qc.refetchQueries({ queryKey: ["invoices", orgId, "page"], type: "active" }),
    ]);
  const invoiceExport = useMutation({
    mutationFn: () => portal.invoiceExport(orgId),
    onSuccess: (blob) =>
      downloadBlob(blob, `${current.organization.name.replace(/[^\w-]+/g, "-").toLowerCase()}-invoices.csv`),
  });

  if (permissionsLoading) return <FullPageSpinner />;
  if (!canRead) {
    return (
      <>
        <PageHeader title="Billing" />
        <Alert tone="info">{missingPermissionReason("billing:read")}</Alert>
      </>
    );
  }
  if (sub.isPending || invoices.isPending || methods.isPending) return <FullPageSpinner />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;
  if (invoices.isError) return <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />;
  if (methods.isError) return <ErrorState error={methods.error} onRetry={() => void methods.refetch()} />;

  const { account } = sub.data;
  const canManage = can("billing:manage");
  const onFree = account.plan.key === "free";
  const setTab = (t: Tab) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });
  const summary = invoices.data;
  const cards = methods.data.payment_methods;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[26px] font-bold text-fg">Billing</h1>
          <p className="mt-1 text-sm text-fg-subtle">
            {current.organization.name} · {account.plan.name} plan
            {tab === "overview" && ` · prices in ${account.plan.currency}, VAT shown separately`}
          </p>
          <DataFreshness className="mt-1" {...freshness} onRefresh={refreshAll} />
        </div>
        {tab === "invoices" ? (
          <Button
            unstyled
            loading={invoiceExport.isPending}
            disabledReason={summary.total === 0 ? "There are no invoices to download yet." : undefined}
            onClick={() => invoiceExport.mutate()}
            className={cx(pill, secondaryPill, "disabled:opacity-50")}
          >
            {invoiceExport.isPending ? "Preparing CSV…" : "Download all (CSV)"}
          </Button>
        ) : onFree ? (
          // On Free, upgrading is the page's main action, so it takes the primary slot.
          <>
            {sub.data.payments_enabled && (
              <LinkButton
                to="/billing/payment-methods/new"
                requires="billing:manage"
                className={cx(pill, secondaryPill)}
              >
                Update payment method
              </LinkButton>
            )}
            <UpgradeLink />
          </>
        ) : (
          <>
            <LinkButton to="/billing/plans" requires="billing:manage" className={cx(pill, secondaryPill)}>
              Upgrade plan
            </LinkButton>
            {sub.data.payments_enabled && (
              <LinkButton
                to="/billing/payment-methods/new"
                requires="billing:manage"
                className={cx(pill, "bg-brand-500 text-white hover:bg-brand-600")}
              >
                Update payment method
              </LinkButton>
            )}
          </>
        )}
      </div>

      {invoiceExport.isError && <ErrorAlert error={invoiceExport.error} />}

      <div role="tablist" className="flex gap-1.5 self-start rounded-xl border border-border bg-brand-50 p-1">
        {TABS.map(([t, label]) => (
          <Button
            unstyled
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cx(
              "rounded-[9px] px-3.5 py-2 text-[13.5px] font-semibold",
              tab === t
                ? "bg-surface text-fg shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                : "text-fg-subtle hover:text-fg",
            )}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "overview" && (
        <Overview subscription={sub.data} invoices={summary} cards={cards} onTab={setTab} />
      )}
      {tab === "invoices" && <FeatureTip placement="billing" />}
      {tab === "invoices" && (
        <InvoicesTab
          account={account}
          anyInvoices={summary.total > 0}
          anyOwed={summary.owed_total > 0}
          cards={cards}
          canManage={canManage}
          provider={sub.data.payment_provider}
          publishableKey={methods.data.publishable_key}
        />
      )}
      {tab === "payment-activity" && <PaymentActivity />}
      {tab === "payment-methods" && <PaymentMethodsTab cards={cards} subscription={sub.data} />}
    </div>
  );
}

// ── overview ───────────────────────────────────────────────────────────────
function Overview({
  subscription,
  invoices,
  cards,
  onTab,
}: {
  subscription: Subscription;
  invoices: InvoiceSummary;
  cards: PaymentMethod[];
  onTab: (t: Tab) => void;
}) {
  const { account, usage, upcoming_charge: charge, vat_rate_percent, payments_enabled } = subscription;
  const plan = account.plan;
  const paid = plan.monthly_price_cents > 0;
  const onFree = plan.key === "free";
  const cancelling = account.scheduled_change?.plan.key === "free";
  const overdue = invoices.owed;
  // The soonest renewal deadline: past it, the plan moves to Free (the API's `Invoice.pay_by`).
  const deadline = invoices.soonest_pay_by;
  const moreOverdue = invoices.owed_total - overdue.length;
  // An upgrade paid by Direct Debit starts once the debit clears.
  const waiting = invoices.waiting_upgrade;
  const card = cards.find((c) => c.is_default) ?? cards[0];

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <section className={cx(panel, "flex flex-col gap-3.5 px-[22px] py-5")}>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-xl font-bold text-fg">{plan.name}</h2>
            {cancelling ? (
              <Badge tone="warning">Ends {formatDate(account.current_period_end)}</Badge>
            ) : (
              <Badge tone="success">Active</Badge>
            )}
            {onFree ? (
              <span className="ml-auto">
                <UpgradeLink />
              </span>
            ) : (
              <LinkButton
                to="/billing/plans"
                requires="billing:manage"
                className="ml-auto text-[12.5px] font-semibold text-brand-500 hover:underline"
              >
                Change plan →
              </LinkButton>
            )}
          </div>
          <p className="text-sm font-semibold text-fg-muted">
            {paid
              ? `${priceLabel(account)} + VAT · ${cancelling ? "ends" : "renews"} ${formatDate(account.current_period_end)}`
              : "No charge · no renewal"}
          </p>
          <div className="flex flex-wrap gap-2">
            {plan.max_custom_domains > 0 && (
              <Chip>
                {usage.custom_domains} of {plan.max_custom_domains} custom domain
                {plan.max_custom_domains === 1 ? "" : "s"} connected
              </Chip>
            )}
            {plan.highlights.map((h) => (
              <Chip key={h}>{h}</Chip>
            ))}
            <HelpHint helpKey={HELP_KEYS.billing.planLimits} label="plan limits" />
          </div>
          {paid && !cancelling && (
            <div className="border-t border-border pt-3.5">
              <LinkButton
                to="/billing/cancel"
                requires="billing:manage"
                className={cx(pill, "border border-[#f2ccc4] bg-surface text-[#b8291f] hover:bg-[#fef0ee]")}
              >
                Cancel {plan.name} plan
              </LinkButton>
            </div>
          )}
        </section>

        <ServicesTable account={account} />

        {account.scheduled_change && (
          <ScheduledChange account={account} waiting={subscription.scheduled_change_waiting} />
        )}
      </div>

      <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[340px]">
        <section className={cx(panel, "flex flex-col gap-3.5 px-[22px] py-5")}>
          <h2 className="text-[15px] font-bold text-fg">Upcoming charges</h2>
          {charge ? (
            <>
              <Line label={`${shortDate(charge.date)} · ${charge.description}`}>
                {formatMoney(charge.amount_cents, charge.currency)}
              </Line>
              <Line label={`VAT (${vat_rate_percent}%)`}>
                {formatMoney(charge.vat_cents, charge.currency)}
              </Line>
              <div className="h-px bg-border" />
              <Line label={`Total on ${shortDate(charge.date)}`} strong>
                {formatMoney(charge.total_cents, charge.currency)}
              </Line>
            </>
          ) : (
            <p className="text-[13px] text-fg-subtle">
              {cancelling
                ? `Nothing more to pay: you move to Free on ${formatDate(account.current_period_end)}.`
                : "Nothing to pay on the Free plan."}
            </p>
          )}
          {waiting && (
            <p role="status" className="rounded-[10px] bg-[#faf6ef] px-3.5 py-3 text-xs text-fg-muted">
              Your upgrade{waiting.plan ? ` to ${planName(waiting.plan)}` : ""} ({waiting.number}) is being
              collected by Direct Debit. It starts once the payment clears, within 7 working days.
            </p>
          )}
          {invoices.owed_total > 0 && (
            <p className="text-xs leading-normal text-fg-subtle">
              {overdue.map((i) => i.description).join(", ")}
              {moreOverdue > 0 && ` and ${moreOverdue} more`} {invoices.owed_total > 1 ? "are" : "is"}{" "}
              overdue, and {invoices.owed_total > 1 ? "are" : "is"} charged separately when you fix the
              payment.
              {deadline && (
                <strong className="font-semibold text-[#b8291f]">
                  {" "}
                  Pay by {formatDate(deadline)}, or the workspace moves to Free.
                </strong>
              )}
            </p>
          )}
        </section>

        <section className={cx(panel, "flex flex-col gap-3.5 px-[22px] py-5")}>
          <h2 className="text-[15px] font-bold text-fg">
            Payment method <HelpHint helpKey={HELP_KEYS.billing.paymentMethods} label="payment methods" />
          </h2>
          {card ? (
            <div
              className={cx(
                "flex items-center gap-2.5 rounded-xl border px-3 py-2.5",
                methodProblem(card) ? "border-[#f2ccc4] bg-[#fef0ee]" : "border-border bg-brand-50",
              )}
            >
              <MethodIcon
                method={card}
                className={methodProblem(card) ? "text-[#b8291f]" : "text-fg-muted"}
              />
              <div className="min-w-0 font-semibold">
                <p className="text-[13.5px] text-fg">{card.label}</p>
                <p className={cx("text-xs", methodProblem(card) ? "text-[#b8291f]" : "text-fg-subtle")}>
                  {methodDetail(card)}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 rounded-xl border border-border bg-brand-50 px-3 py-2.5">
              <CreditCard className="size-[18px] text-fg-subtle" aria-hidden />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-fg">No card on file</p>
                <p className="text-xs text-fg-subtle">
                  {payments_enabled
                    ? "A card is needed for paid plans and domains."
                    : "Online payments are coming soon. Contact sales to pay by invoice."}
                </p>
              </div>
            </div>
          )}
          {payments_enabled && (
            <LinkButton
              to="/billing/payment-methods/new"
              requires="billing:manage"
              className={cx(pill, "self-start bg-brand-500 text-white hover:bg-brand-600")}
            >
              {card ? "Update card" : "Add card"}
            </LinkButton>
          )}
        </section>

        <section className={cx(panel, "flex flex-col gap-3.5 px-[22px] py-5")}>
          <div className="flex items-start">
            <h2 className="text-[15px] font-bold text-fg">
              Recent invoices <HelpHint helpKey={HELP_KEYS.billing.invoicesAndVat} label="invoices and VAT" />
            </h2>
            <Button
              unstyled
              type="button"
              onClick={() => onTab("invoices")}
              className="ml-auto text-[12.5px] font-semibold text-brand-500 hover:underline"
            >
              View all →
            </Button>
          </div>
          {invoices.total === 0 ? (
            <p className="text-[12.5px] text-fg-subtle">
              No invoices yet. They appear here after your first payment.
            </p>
          ) : (
            invoices.recent.map((i) => (
              <div key={i.id} className="flex items-center gap-2 text-[12.5px]">
                <span className="min-w-0 flex-1 truncate text-fg">
                  {shortDate(i.issued_at)} · {i.description}
                </span>
                <span className="font-semibold text-fg">{formatMoney(i.total_cents, i.currency)}</span>
                <InvoiceStatusPill status={i.status} />
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Free plan's way up: the Growth and Pro comparison. brand-600, not brand-500: white on brand-500 is
 * 3.6:1, under WCAG AA's 4.5:1 for 13px text.
 */
function UpgradeLink() {
  return (
    <LinkButton
      to="/billing/plans"
      requires="billing:manage"
      className={cx(pill, "gap-1.5 bg-brand-600 text-white hover:bg-brand-700")}
    >
      Upgrade plan
      <ArrowUpRight className="size-4" aria-hidden />
    </LinkButton>
  );
}

function priceLabel(account: CustomerAccount): string {
  const { plan, billing_period: period } = account;
  return period === "annual"
    ? `${formatMoney(plan.annual_price_cents, plan.currency)} / year`
    : `${formatMoney(plan.monthly_price_cents, plan.currency)} / month`;
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-[5px] text-xs text-fg-muted">
      <Check className="size-3 text-[#12a97e]" aria-hidden />
      {children}
    </span>
  );
}

function Line({ label, strong = false, children }: { label: string; strong?: boolean; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-[13px]">
      <span className="text-fg-subtle">{label}</span>
      <span className={cx("ml-auto text-fg", strong && "font-semibold")}>{children}</span>
    </div>
  );
}

/** Every service and its own renewal clock: the base plan, plus the custom domains it covers. */
function ServicesTable({ account }: { account: CustomerAccount }) {
  const domains = useOrganizationDomains();
  const { plan } = account;
  const paid = plan.monthly_price_cents > 0;
  return (
    <section className={cx(panel, "overflow-hidden")}>
      <div className="px-[22px] pt-[18px] pb-3">
        <h2 className="text-base font-bold text-fg">Add-ons and renewal dates</h2>
        <p className="text-[12.5px] text-fg-subtle">Every service here keeps its own renewal clock</p>
      </div>
      {/* Focusable so keyboard users can scroll the wide table (axe: scrollable-region-focusable). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className="overflow-x-auto" role="region" tabIndex={0} aria-label="Add-ons and renewal dates">
        <Table className="w-full min-w-[560px] text-left">
          <TableHead className="bg-brand-50 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
            <TableRow>
              <TableHeaderCell className="px-[22px] py-2 font-semibold">Service</TableHeaderCell>
              <TableHeaderCell className="w-[130px] py-2 font-semibold">Price</TableHeaderCell>
              <TableHeaderCell className="w-[130px] py-2 font-semibold">Renews</TableHeaderCell>
              <TableHeaderCell className="w-[120px] py-2 pr-[22px] font-semibold">Status</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody className="divide-y divide-border border-t border-border">
            <TableRow>
              <TableCell className="px-[22px] py-3">
                <p className="text-[13.5px] font-semibold text-fg">{plan.name} plan</p>
                <p className="text-xs text-fg-subtle">Base plan · {account.billing_period}</p>
              </TableCell>
              <TableCell className="text-[12.5px] text-fg-muted">
                {paid ? priceLabel(account).replace(" / month", " / mo").replace(" / year", " / yr") : "Free"}
              </TableCell>
              <TableCell className="text-[12.5px] font-semibold text-fg">
                {paid ? formatDate(account.current_period_end) : "Doesn’t renew"}
              </TableCell>
              <TableCell className="pr-[22px]">
                {account.scheduled_change?.plan.key === "free" ? (
                  <Badge tone="warning">Cancels</Badge>
                ) : (
                  <Badge tone="success">Active</Badge>
                )}
              </TableCell>
            </TableRow>
            {domains.data?.domains.map(({ domain }) => (
              <TableRow key={domain.id}>
                <TableCell className="px-[22px] py-3">
                  <p className="text-[13.5px] font-semibold text-fg">{domain.hostname}</p>
                  <p className="text-xs text-fg-subtle">Custom domain · connected</p>
                </TableCell>
                <TableCell className="text-[12.5px] text-fg-muted">Included</TableCell>
                <TableCell className="text-[12.5px] font-semibold text-fg">With the plan</TableCell>
                <TableCell className="pr-[22px]">
                  <DomainStatusBadge status={domain.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="px-[22px] pt-3 pb-4">
        <Link to="/domains" className="text-[12.5px] font-semibold text-brand-500 hover:underline">
          + Connect a domain
        </Link>
      </div>
    </section>
  );
}

function ScheduledChange({ account, waiting }: { account: CustomerAccount; waiting: boolean }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const undo = useMutation({
    mutationFn: () => portal.undoScheduledChange(orgId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["subscription", orgId] });
      await qc.invalidateQueries({ queryKey: ["overview", orgId] });
      toast.show({ tone: "success", title: "Scheduled change cancelled" });
    },
  });
  const change = account.scheduled_change!;
  const when = formatDate(change.effective_at);
  const title =
    change.plan.key === "free"
      ? `Your ${account.plan.name} plan ends on ${when}`
      : `Your plan moves to ${change.plan.name} (${change.billing_period}) on ${when}`;
  return (
    <section className={cx(panel, "flex flex-col gap-3.5 px-[22px] py-5")}>
      <h2 className="text-base font-bold text-fg">Scheduled changes</h2>
      <ErrorAlert error={undo.error} />
      <div className="flex items-center gap-3 rounded-xl bg-brand-50 px-3.5 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-fg">{title}</p>
          <p className="text-xs text-fg-muted">
            You chose this on {formatDate(change.chosen_at)}.{" "}
            {waiting
              ? `Disconnect custom domains to fit ${change.plan.name} (${change.plan.max_custom_domains} allowed) before then, or the change will wait.`
              : "Until then everything stays as it is."}
          </p>
        </div>
        <Button
          unstyled
          requires="billing:manage"
          loading={undo.isPending}
          onClick={() => undo.mutate()}
          className="shrink-0 text-[12.5px] font-semibold text-brand-500 hover:underline disabled:opacity-60"
        >
          Undo
        </Button>
      </div>
    </section>
  );
}

// ── invoices ───────────────────────────────────────────────────────────────
const SERVICE_FILTERS = { all: "Service: all", plan: "Service: plans", refund: "Service: refunds" } as const;
const STATUS_FILTERS = {
  all: "Status: all",
  paid: "Status: paid",
  failed: "Status: failed",
  pending: "Status: processing",
  refunded: "Status: refunded",
  void: "Status: cancelled",
} as const;
const RANGE_FILTERS = { "12m": "Last 12 months", all: "All time" } as const;
const keys = <T extends string>(options: Record<T, string>) => Object.keys(options) as T[];
/**
 * The Invoices view kept in the URL (PHP-3341), next to `?tab=invoices`: `?service=` (default all),
 * `?status=` (default all) and `?period=` (default the last 12 months). Invoices are listed newest
 * first, as the API returns them. Changing tab starts the other tab afresh.
 */
const INVOICE_VIEW = {
  service: choice("service", keys(SERVICE_FILTERS), "all"),
  status: choice("status", keys(STATUS_FILTERS), "all"),
  range: choice("period", keys(RANGE_FILTERS), "12m"),
};

function FilterPill<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Record<T, string>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <span className="relative inline-flex">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="h-[33px] cursor-pointer appearance-none rounded-full border border-border bg-surface py-2 pr-8 pl-3 text-[12.5px] font-semibold text-fg-muted hover:bg-muted"
      >
        {(Object.keys(options) as T[]).map((k) => (
          <option key={k} value={k}>
            {options[k]}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-muted"
        aria-hidden
      />
    </span>
  );
}

/** Figma "Billing — Invoices (PRT-014)". */
function InvoicesTab({
  account,
  anyInvoices,
  anyOwed,
  cards,
  canManage,
  provider,
  publishableKey,
}: {
  account: CustomerAccount;
  /** The organization has invoices at all (whatever the filters say). */
  anyInvoices: boolean;
  /** Some invoice is failed and owed. */
  anyOwed: boolean;
  cards: PaymentMethod[];
  canManage: boolean;
  provider: string;
  publishableKey: string | null | undefined;
}) {
  const { orgId, current } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [{ service, status, range }, setView] = useUrlView(INVOICE_VIEW);
  const [editing, setEditing] = useState(false);
  // The invoice whose payment history is open.
  const [historyOf, setHistoryOf] = useState<string | null>(null);
  // "Pay now" is a payment made on the page: a default Apple Pay card is kept for renewals, so it
  // pays with the first card that can.
  const payWith = payableDefault(cards);
  const pay = useMutation({
    mutationFn: async (id: string) => {
      try {
        return await portal.payInvoice(orgId, id, payWith?.id);
      } catch (err) {
        // The bank wants the customer to approve it: take them there, then finish the same payment.
        const approval = approvalNeededBy(err);
        if (!approval) throw err;
        await approvePayment(approval, orgId, publishableKey);
        return portal.invoice(orgId, id);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["invoices", orgId] });
    },
    // A declined try is recorded too, so the open history refreshes whatever the answer.
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ["payment-attempts", orgId] });
      await qc.invalidateQueries({ queryKey: ["payment-activity", orgId] });
    },
  });
  // "Last 12 months" is from the start of the day a year ago, so the query stays the same all day.
  const issuedFrom = useMemo(() => {
    if (range === "all") return undefined;
    const since = new Date();
    since.setFullYear(since.getFullYear() - 1);
    since.setHours(0, 0, 0, 0);
    return since.toISOString();
  }, [range]);
  const pager = useCursorPager(`${service}|${status}|${range}`);
  const filters = {
    service: service === "all" ? undefined : service,
    status: status === "all" ? undefined : status,
    issuedFrom,
  };
  const pageKey = (token: string | null) => [
    "invoices",
    orgId,
    "page",
    { ...filters, size: pager.pageSize, token },
  ];
  const fetchPage = (token: string | null) =>
    portal.invoicesPage(orgId, { ...filters, pageSize: pager.pageSize, pageToken: token });
  const list = useQuery({
    queryKey: pageKey(pager.token),
    queryFn: () => fetchPage(pager.token),
    placeholderData: keepPreviousData,
    refetchInterval: AUTO_REFRESH_MS,
  });
  usePrefetchNext(list.data?.next_page_token, pageKey, fetchPage);
  const shown = list.data?.invoices ?? [];
  const filtered = service !== "all" || status !== "all" || range !== "all";
  const clearFilters = () => setView({ service: "all", status: "all", range: "all" });

  const billedTo = account.company_name || current.organization.name;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <FilterPill
          label="Service"
          value={service}
          options={SERVICE_FILTERS}
          onChange={(v) => setView({ service: v })}
        />
        <FilterPill
          label="Status"
          value={status}
          options={STATUS_FILTERS}
          onChange={(v) => setView({ status: v })}
        />
        <FilterPill
          label="Period"
          value={range}
          options={RANGE_FILTERS}
          onChange={(v) => setView({ range: v })}
        />
        <CopyLinkButton />
        {list.data && (
          <span className="text-xs text-fg-subtle" aria-live="polite">
            {list.data.total.toLocaleString("en-GB")} {list.data.total === 1 ? "invoice" : "invoices"}
          </span>
        )}
        <p className="ml-auto text-xs text-fg-subtle">
          Billing details: {billedTo}
          {account.billing_email && ` · ${account.billing_email}`}
          {" · "}
          <Button
            unstyled
            requires="billing:manage"
            onClick={() => setEditing(true)}
            className="font-semibold text-brand-500 hover:underline"
          >
            Edit
          </Button>
        </p>
      </div>
      <ErrorAlert error={pay.error} />
      {/* "Pay now" charges a card on file, so say who processes it before anyone clicks. */}
      {canManage && anyOwed && <PoweredByStripe provider={provider} />}

      <section className={cx(panel, "overflow-hidden px-0.5")}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- see ServicesTable */}
        <div className="overflow-x-auto" role="region" tabIndex={0} aria-label="Invoice history">
          <Table className="w-full min-w-[860px] text-left text-[12.5px]">
            <TableHead className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
              <TableRow>
                <TableHeaderCell className="w-[152px] pt-3.5 pb-2.5 pl-[22px] font-semibold">
                  Invoice
                </TableHeaderCell>
                <TableHeaderCell className="w-[110px] pt-3.5 pb-2.5 font-semibold">Date</TableHeaderCell>
                <TableHeaderCell className="pt-3.5 pb-2.5 font-semibold">For</TableHeaderCell>
                <TableHeaderCell className="w-[120px] pt-3.5 pb-2.5 font-semibold">Net</TableHeaderCell>
                <TableHeaderCell className="w-[120px] pt-3.5 pb-2.5 font-semibold">
                  Total (inc. VAT)
                </TableHeaderCell>
                <TableHeaderCell className="w-[110px] pt-3.5 pb-2.5 font-semibold">Status</TableHeaderCell>
                <TableHeaderCell className="w-[112px] pt-3.5 pr-[22px] pb-2.5" aria-label="Actions" />
              </TableRow>
            </TableHead>
            <TableBody className="divide-y divide-border border-t border-border">
              {shown.map((i) => (
                <Fragment key={i.id}>
                  <TableRow>
                    <TableCell className="py-3 pl-[22px] font-semibold text-fg">
                      {i.number}
                      {i.kind !== "refund" && (
                        <Button
                          unstyled
                          type="button"
                          aria-expanded={historyOf === i.id}
                          onClick={() => setHistoryOf(historyOf === i.id ? null : i.id)}
                          className="mt-0.5 block text-[11px] font-semibold text-brand-500 hover:underline"
                        >
                          {historyOf === i.id ? "Hide payment history" : "Payment history"}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="py-3 text-fg-muted">{formatDate(i.issued_at)}</TableCell>
                    <TableCell className="py-3 text-fg">{i.description}</TableCell>
                    <TableCell className="py-3 text-fg-muted">{signed(i, i.net_cents)}</TableCell>
                    <TableCell className="py-3 font-semibold text-fg">{signed(i, i.total_cents)}</TableCell>
                    <TableCell className="py-3">
                      <InvoiceStatusPill status={i.status} />
                    </TableCell>
                    <TableCell className="py-3 pr-[22px] text-right font-semibold">
                      {owed(i) ? (
                        <Button
                          unstyled
                          requires="billing:manage"
                          loading={pay.isPending}
                          onClick={() => pay.mutate(i.id)}
                          className="text-brand-500 hover:underline disabled:opacity-60"
                        >
                          {pay.isPending && pay.variables === i.id ? "Paying…" : "Pay now →"}
                        </Button>
                      ) : (
                        <a
                          href={`/billing/invoices/${i.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-500 hover:underline"
                        >
                          PDF ↓
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                  {historyOf === i.id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted px-[22px] py-4">
                        <PaymentAttempts orgId={orgId} invoiceId={i.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        {list.isPending && (
          <div className="flex justify-center border-t border-border px-6 py-10">
            <Spinner />
          </div>
        )}
        {list.isError && (
          <div className="flex flex-col items-start gap-3 border-t border-border px-[22px] py-5">
            <ErrorAlert error={list.error} />
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          </div>
        )}
        {list.data && shown.length === 0 && anyInvoices && (
          <div className="border-t border-border">
            <EmptyState
              title="No invoices match these filters"
              description="Try a different service, status or time period."
              action={
                filtered && (
                  <Button variant="secondary" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )
              }
            />
          </div>
        )}
        {!anyInvoices ? (
          <div className="border-t border-border">
            <EmptyState
              icon={<FileText className="size-5" aria-hidden />}
              title="No invoices yet"
              description="An invoice is issued for every payment, with VAT shown separately."
              action={
                <Button variant="secondary" size="sm" onClick={() => navigate("/billing/plans")}>
                  See plans
                </Button>
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 border-t border-border px-[22px] py-3.5">
            <Pager
              pager={pager}
              shown={shown.length}
              total={list.data?.total}
              hasNext={!!list.data?.next_page_token}
              onNext={() => list.data?.next_page_token && pager.next(list.data.next_page_token)}
              loading={list.isFetching}
              noun="invoices"
            />
            <p className="text-xs text-fg-subtle">Invoices include VAT and your billing details.</p>
          </div>
        )}
      </section>

      <Modal open={editing} onClose={() => setEditing(false)} title="Billing details">
        <BillingProfile key={account.version} account={account} onSaved={() => setEditing(false)} />
      </Modal>
    </div>
  );
}

const signed = (i: Invoice, cents: number) =>
  `${i.kind === "refund" ? "−" : ""}${formatMoney(Math.abs(cents), i.currency)}`;

// ── payment methods ───────────────────────────────────────────────────────
function PaymentMethodsTab({ cards, subscription }: { cards: PaymentMethod[]; subscription: Subscription }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const paymentsEnabled = subscription.payments_enabled;
  const [removing, setRemoving] = useState<PaymentMethod | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
  const toast = useToast();
  const makeDefault = useMutation({
    mutationFn: (id: string) => portal.makeDefaultPaymentMethod(orgId, id),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: "Default payment method changed" });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => portal.removePaymentMethod(orgId, id),
    onSuccess: async () => {
      await refresh();
      setRemoving(null);
      toast.show({ tone: "success", title: "Payment method removed" });
    },
  });
  return (
    <section className={cx(panel, "flex max-w-3xl flex-col gap-3.5 px-[22px] py-5")}>
      <div className="flex items-center">
        <h2 className="text-base font-bold text-fg">Cards on file</h2>
        {paymentsEnabled && (
          <LinkButton
            to="/billing/payment-methods/new"
            requires="billing:manage"
            className={cx(pill, "ml-auto bg-brand-500 text-white hover:bg-brand-600")}
          >
            Add a payment method
          </LinkButton>
        )}
      </div>
      <ErrorAlert error={makeDefault.error} />
      {cards.length === 0 ? (
        <p className="flex items-center gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted">
          <CreditCard className="size-[18px] shrink-0" aria-hidden />
          {paymentsEnabled
            ? "No cards yet. The first one you add becomes the default for anything you buy."
            : "Online payments are coming soon. Contact sales to pay by invoice."}
        </p>
      ) : (
        cards.map((m) => (
          <div
            key={m.id}
            className={cx(
              "flex flex-wrap items-center gap-3 rounded-[10px] px-3.5 py-3",
              methodProblem(m) ? "border border-[#f2ccc4] bg-[#fef0ee]" : "bg-[#faf6ef]",
            )}
          >
            <MethodIcon method={m} className={methodProblem(m) ? "text-[#b8291f]" : "text-fg-muted"} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">{m.label}</p>
              <p
                className={cx(
                  "text-xs",
                  methodProblem(m) ? "font-semibold text-[#b8291f]" : "text-[#766e6a]",
                )}
              >
                {methodDetail(m)}
                {m.holder_name && ` · ${m.holder_name}`}
                {m.renewals_only && m.kind === "card" && " · Used for renewals"}
              </p>
            </div>
            <span className="ml-auto flex items-center gap-4 text-xs font-semibold">
              {m.is_default ? (
                <span className="text-[#766e6a]">Default</span>
              ) : (
                <Button
                  unstyled
                  requires="billing:manage"
                  loading={makeDefault.isPending}
                  onClick={() => makeDefault.mutate(m.id)}
                  className="text-brand-500 hover:underline"
                >
                  Make default
                </Button>
              )}
              <Button
                unstyled
                requires="billing:manage"
                aria-label={`Remove ${m.label}`}
                loading={remove.isPending}
                onClick={() => {
                  remove.reset();
                  setRemoving(m);
                }}
                className="text-[#b8291f] hover:underline"
              >
                Remove
              </Button>
            </span>
          </div>
        ))
      )}
      {removing && (
        <RemoveMethodDialog
          method={removing}
          cards={cards}
          subscription={subscription}
          pending={remove.isPending}
          error={remove.error}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

/** How many renewals the remove dialog lists before "and N more". */
const LISTED_RENEWALS = 5;

/**
 * What removing a saved method does to the renewals, decided as the API decides it (`remove_method`): while the
 * plan renews, the last method that can pay stays; removing the default hands it to the newest one left.
 */
function removalImpact(method: PaymentMethod, cards: PaymentMethod[], { account }: Subscription) {
  const next = account.scheduled_change?.plan ?? account.plan;
  const renews = account.plan.monthly_price_cents > 0 && next.monthly_price_cents > 0;
  const others = cards.filter((c) => c.id !== method.id);
  return {
    renews,
    blocked: renews && others.every(methodProblem),
    // Cards come newest first, the order the API picks the next default in.
    payer: method.is_default ? others[0] : cards.find((c) => c.is_default),
  };
}

/** What removing a payment method does to the real renewal, before anything is removed. */
function RemoveMethodDialog({
  method,
  cards,
  subscription,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  method: PaymentMethod;
  cards: PaymentMethod[];
  subscription: Subscription;
  pending: boolean;
  error: Error | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { account, upcoming_charge: charge } = subscription;
  const { renews, blocked, payer } = removalImpact(method, cards, subscription);
  const domains = useOrganizationDomains();
  const plan = account.scheduled_change?.plan ?? account.plan;
  const date = formatDate(account.current_period_end);
  const renewal = `${plan.name} renewal on ${date}${charge ? ` (${formatMoney(charge.total_cents, charge.currency)})` : ""}`;

  let impact: string;
  if (!renews)
    impact =
      account.plan.monthly_price_cents > 0
        ? `Your ${account.plan.name} plan ends on ${date} and doesn’t renew, so nothing scheduled is charged to it.`
        : "Your plan doesn’t renew, so nothing scheduled is charged to it.";
  else if (blocked)
    impact = `Your ${renewal} will fail without it: it’s the only payment method that can pay. Add another one first, or cancel the plan.`;
  else if (method.is_default && payer)
    impact = `${payer.label} becomes the default and pays your ${renewal}.${
      methodProblem(payer) ? " It can’t be charged as it is, so make a working one the default first." : ""
    }`;
  else
    impact = `Your ${renewal} stays on ${payer?.label ?? "the default"}. Nothing scheduled is charged to this one.`;

  // The renewals this method pays: the plan, and the custom domains that renew with it.
  const affected = renews && (method.is_default || blocked);
  const included = domains.data?.domains ?? [];
  const listed = included.slice(0, LISTED_RENEWALS - 1);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Remove ${method.label}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {blocked ? (
            subscription.payments_enabled && (
              <Link to="/billing/payment-methods/new" className={buttonClass("primary")}>
                Add a payment method
              </Link>
            )
          ) : (
            <Button variant="danger" loading={pending} onClick={onConfirm}>
              Remove
            </Button>
          )}
        </>
      }
    >
      <ErrorAlert error={error} />
      {blocked ? <Alert tone="warning">{impact}</Alert> : <p className="text-sm text-fg-muted">{impact}</p>}
      {affected && (
        <div>
          <p className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">Renewals it pays</p>
          <ul className="mt-2 divide-y divide-border rounded-lg border border-border text-sm">
            <li className="flex justify-between gap-3 px-3 py-2">
              <span className="font-medium text-fg">{plan.name} plan</span>
              <span className="text-fg-muted">{date}</span>
            </li>
            {listed.map(({ domain }) => (
              <li key={domain.id} className="flex justify-between gap-3 px-3 py-2">
                <span className="truncate text-fg">{domain.hostname}</span>
                <span className="shrink-0 text-fg-muted">With the plan</span>
              </li>
            ))}
            {included.length > listed.length && (
              <li className="px-3 py-2 text-fg-muted">and {included.length - listed.length} more domains</li>
            )}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function BillingProfile({ account, onSaved }: { account: CustomerAccount; onSaved: () => void }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({
    billing_email: account.billing_email ?? "",
    company_name: account.company_name ?? "",
    country: account.country ?? "",
  });
  const save = useMutation({
    mutationFn: () =>
      portal.updateBillingProfile(orgId, {
        billing_email: form.billing_email.trim() || null,
        company_name: form.company_name.trim() || null,
        country: form.country.trim().toUpperCase() || null,
        version: account.version,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["subscription", orgId] });
      toast.show({ tone: "success", title: "Billing details saved" });
      onSaved();
    },
  });
  return (
    <Form
      className="space-y-4"
      error={save.error}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-fg-muted">Shown on invoices and used for billing e-mails.</p>
      <ErrorAlert error={save.error} />
      <Field label="Company name" name="company_name">
        {(p) => (
          <Input
            {...p}
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            maxLength={200}
          />
        )}
      </Field>
      <Field label="Billing e-mail" name="billing_email">
        {(p) => (
          <Input
            {...p}
            type="email"
            value={form.billing_email}
            onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
          />
        )}
      </Field>
      <Field label="Country" name="country" hint="Two-letter code, e.g. GB, US, BD.">
        {(p) => (
          <Input
            {...p}
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
            maxLength={2}
            pattern="[A-Za-z]{2}"
            title="Enter a two-letter country code, like GB"
          />
        )}
      </Field>
      <Button type="submit" loading={save.isPending}>
        Save details
      </Button>
    </Form>
  );
}
